"use client";
import { Popover, PopoverContent, PopoverTrigger } from "@nextui-org/react";
import dayjs from "dayjs";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { fetchFilteredCourses } from "../../../actions/courses";
import data from "../../../assets/common.json";
import DownloadBrochure from "../../../components/modal/DownloadBrochure";
import {
  AboutContent,
  CardBody,
  CardContent,
  CardDesc,
  CardFooter,
  CardImg,
  CardSubtitle,
  CardTitle,
  CategoryBtn,
  CommonCard,
  DateContainer,
  HighLight,
  Para,
  ScheduledTime,
  SecondaryBtn,
  SecondaryOuter,
  Stack,
} from "../../../components/molecules";
import StarRating from "../../../components/StarRating";
import { useDictionary } from "../../../context/dictionaryProvider";
import {
  convertTimeToAMPM,
  getImageUrl,
  getImageUrlNew,
  getLink,
} from "../../../lib/util";
import dynamic from "next/dynamic";
const DescriptionParser = dynamic(
  () => import("../../../components/courses/DescriptionParser"),
  {
    ssr: false,
  }
);

const OverLapImg = ({ fill }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="174"
      height="87"
      viewBox="0 0 174 87"
      fill="none"
      className="accreOverImg"
    >
      <path
        d="M33.6774 73.2373C33.6774 88.5536 10.9585 86.6723 0 87L174 87L174 1.47245e-06C174 8.42099 164.208 13.4184 156.722 15.6514C153.851 16.5079 150.843 16.7119 147.846 16.7119L65.6774 16.7119C48.0043 16.7119 33.6774 31.0388 33.6774 48.7119L33.6774 73.2373Z"
        fill={`${fill ? fill : "#316BCB"}`}
      />
    </svg>
  );
};

const CoursesGrid = ({
  type,
  initialCourses = [],
  searchParams,
  initialPagination,
  params,
}) => {
  const dict = useDictionary();
  const rating = 4.5;
  const [pagination, setPagination] = useState({
    ...initialPagination,
    start: initialPagination?.start + initialPagination?.limit,
  });
  const [courses, setCourses] = useState(initialCourses);

  useEffect(() => setCourses(initialCourses), [initialCourses]);

  // const ImgWrap = ({ src, alt, classNames }) => {
  //   return (
  //     <img
  //       loading="lazy"
  //       src={src}
  //       alt={alt.toString()}
  //       className={`h-[290px] object-cover ${classNames}`}
  //       style={{ width: "100%", borderRadius: "10px" }}
  //     />
  //   );
  // };

  const handleClick = async () => {
    const res = await fetchFilteredCourses(
      type,
      {
        ...searchParams,
        limit: pagination?.limit,
        start: pagination?.start,
      },
      params
    );
    if (res) {
      setPagination((prev) => ({
        ...prev,
        start: prev.start + prev.limit,
        total: res?.meta?.pagination.total,
      }));
      setCourses([...courses, ...(res?.data || [])]);
    }
  };

  return (
    <Stack>
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:mt-0">
        {courses?.map((course, index) => (
          <Popover
            placement="right"
            key={index}
            className="rounded-3xl overflow-hidden"
            backdrop="blur"
            shouldBlockScroll
          >
            <li>
              <CommonCard>
                <PopoverTrigger>
                  <>
                    <div className="relative h-[290px] w-full overflow-hidden">
                      {course.thumbnail.url ? (
                        <Image
                          src={getImageUrlNew(course.thumbnail, "small" || "")}
                          alt=""
                          fill
                          className="rounded-[10px] w-full h-full"
                          loading="lazy"
                          objectFit="cover"
                          objectPosition="center"
                        />
                      ) : (
                        <Image
                          src="https://d3v381xb6gm5e.cloudfront.net/media/13969942_de24873a03.png"
                          alt=""
                          fill
                          className="rounded-[10px] bg-[#0000001a] p-12"
                          loading="lazy"
                          objectFit="contain"
                        />
                      )}
                    </div>
                    {course?.category?.name && (
                      <CategoryBtn>
                        <h2>{course?.category?.name}</h2>
                      </CategoryBtn>
                    )}
                  </>
                </PopoverTrigger>
                <CardContent>
                  <Link
                    prefetch={false}
                    href={getLink(`/${params?.lang}/${course?.slug}`)}
                    key={index}
                  >
                    <CardTitle className="md:h-[51px] overflow-hidden">
                      {course?.course_name}
                    </CardTitle>
                    <CardSubtitle className="flex items-center gap-2">
                      {dict?.courses.accreditedBy}{" "}
                      <div className="flex items-center gap-3">
                        {course?.affiliations?.data
                          ?.slice(0, 4)
                          .map((aff, index) => (
                            <React.Fragment key={index}>
                              <Image
                                loading="lazy"
                                alt=""
                                src={getImageUrl(aff.logo)}
                                height={40}
                                width={50}
                              />
                            </React.Fragment>
                          ))}
                      </div>
                    </CardSubtitle>
                    <CardDesc className="fourLineText">
                      {course?.short_description}
                    </CardDesc>
                    <Stack className="gap-5">
                      {course?.rating && (
                        <>
                          <StarRating rating={rating} />
                          <p>{course?.rating}/5</p>
                        </>
                      )}
                    </Stack>
                  </Link>
                  {course?.brochure?.url && (
                    <DownloadBrochure
                      document={course?.brochure}
                      courseId={course?.id}
                      type="standard"
                      course={course}
                    />
                  )}
                </CardContent>
              </CommonCard>
            </li>
            <PopoverContent className="p-0 overflow-hidden">
              <CommonCard className="sessionCard !border-none " key={index}>
                <CardContent session>
                  <CardBody session>
                    <AboutContent className="max-w-[540px]">
                      <h3 className="capitalize relative text-lg font-semibold tracking-[0.54px] text-white">
                        {course?.course_name}
                      </h3>
                      <Stack className="gap-5 my-3">
                        <div>
                          {dict?.courses.accreditedBy}{" "}
                          {course?.affiliations?.data.map((aff, index) => (
                            <React.Fragment key={index}>
                              <HighLight>{aff.name}</HighLight>{" "}
                            </React.Fragment>
                          ))}
                        </div>

                        <StarRating rating={rating} />
                        <p>4.78/5</p>
                      </Stack>
                      <DescriptionParser
                        description={course?.course_description}
                      />
                      {course?.sessions?.data?.length > 0 && (
                        <h3 className="capitalize text-xl my-3 relative font-semibold tracking-[0.54px] text-black">
                          Next Sessions
                        </h3>
                      )}
                      <Stack gap="10px">
                        {course?.sessions?.data?.map((slot, index) => (
                          <div key={index}>
                            <Para className="capitalize mb-1">
                              {slot?.type} training
                            </Para>
                            <DateContainer key={index} value={slot?.id}>
                              <p className="">
                                {dayjs(slot?.start_date).format(
                                  "DD MMMM, dddd"
                                )}
                              </p>
                              <ScheduledTime className="mt-2">
                                {convertTimeToAMPM(slot?.slots[0]?.start_time)}{" "}
                                - {convertTimeToAMPM(slot?.slots[0]?.end_time)}
                              </ScheduledTime>
                            </DateContainer>
                          </div>
                        ))}
                      </Stack>
                    </AboutContent>
                  </CardBody>
                  <CardFooter
                    session
                    color="#fff"
                    style={{ padding: "38px 40px" }}
                  >
                    {course?.discounted_price ? (
                      <>
                        <p className="text-[24px] font-[700] tracking-[0.72px]">
                          AED{course?.discounted_price}
                        </p>
                        <strike className="text-[16px] text-[#9F9F9F] font-[400] tracking-[0.48px]">
                          AED {course?.price}
                        </strike>
                      </>
                    ) : (
                      <p className="text-[24px] font-[700] tracking-[0.72px]">
                        {course?.price ? "AED " + course?.price : undefined}
                      </p>
                    )}
                  </CardFooter>
                  <Link
                    prefetch={false}
                    href={getLink(
                      `/${params?.lang}/browsecourses/${course?.category?.slug}/${course?.slug}/classroom`
                    )}
                  >
                    <SecondaryBtn className="secondaryOverlap" session>
                      {data.applyNow}
                    </SecondaryBtn>
                  </Link>
                  <OverLapImg fill="#ffffffb0" />
                </CardContent>
              </CommonCard>
            </PopoverContent>
          </Popover>
        ))}
      </ul>
      {pagination?.total > courses?.length && (
        <SecondaryOuter className="mt-8 mb-8 mx-auto" onClick={handleClick}>
          {dict?.loadMore}
        </SecondaryOuter>
      )}
    </Stack>
  );
};

export default CoursesGrid;
